import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, ExternalLink, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { logError } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrphanedPaymentsTab = ({ adminToken }) => {
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconcilingId, setReconcilingId] = useState(null);

  const loadOrphans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payments/orphaned`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrphans(data.orphans || []);
      } else {
        logError('Orphans fetch non-OK', { status: res.status });
      }
    } catch (err) {
      logError('Failed to load orphaned payments', err);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) loadOrphans();
  }, [adminToken, loadOrphans]);

  const markReconciled = async (sessionId) => {
    const note = window.prompt(
      'Optional note about how this payment was reconciled (visible to admins only):',
      'Manually contacted customer; booking recovered.',
    );
    if (note === null) return;
    setReconcilingId(sessionId);
    try {
      const res = await fetch(`${API_URL}/api/admin/payments/${sessionId}/mark-reconciled`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ note }),
      });
      if (res.ok) {
        await loadOrphans();
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logError('Mark-reconciled failed', err);
      alert('Network error while marking reconciled.');
    } finally {
      setReconcilingId(null);
    }
  };

  const totalOrphanedAmount = orphans.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);

  return (
    <div data-testid="admin-orphaned-payments-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>Orphaned Payments</h2>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
            Paid Stripe transactions that don&apos;t have a matching booking. Usually caused by browser closes mid-confirmation.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {orphans.length} orphan{orphans.length === 1 ? '' : 's'} ·{' '}
            <strong style={{ color: '#b45309' }}>${totalOrphanedAmount.toFixed(2)}</strong> unreconciled
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={loadOrphans}
            data-testid="refresh-orphans-btn"
            style={{ fontSize: '0.75rem' }}
          >
            <RefreshCw size={14} style={{ marginRight: '0.4rem' }} />
            Refresh
          </Button>
        </div>
      </div>

      {orphans.length === 0 && !loading && (
        <Card style={{ padding: '2.5rem', textAlign: 'center', border: '2px solid #d1fae5', backgroundColor: '#f0fdf4' }}>
          <CheckCircle size={48} color="#10b981" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#065f46', marginBottom: '0.4rem' }}>
            All payments accounted for
          </h3>
          <p style={{ color: '#047857', fontSize: '0.9rem', margin: 0 }}>
            Every successful Stripe payment has a matching booking record.
          </p>
        </Card>
      )}

      {loading && (
        <Card style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading…</Card>
      )}

      {!loading && orphans.length > 0 && (
        <Card>
          <CardContent style={{ padding: 0, overflowX: 'auto' }}>
            <div style={{ minWidth: '900px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#fef3c7', borderBottom: '2px solid #f59e0b' }}>
                  <tr>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#78350f' }}>Source</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#78350f' }}>Paid On</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#78350f' }}>Customer Email</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#78350f' }}>Pickup</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, color: '#78350f' }}>Amount</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#78350f' }}>Session</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#78350f' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.map((o) => (
                    <tr key={o.session_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{
                          padding: '0.2rem 0.55rem',
                          borderRadius: '9999px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: o.source === 'subscription' ? '#dbeafe' : '#fef3c7',
                          color: o.source === 'subscription' ? '#1e40af' : '#92400e',
                        }}>
                          {o.source === 'subscription' ? 'Subscription' : 'One-time'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {o.created_at ? new Date(o.created_at).toLocaleString() : 'N/A'}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.8rem', fontWeight: 600 }}>
                        {o.customer_email || <span style={{ color: '#9ca3af', fontWeight: 400 }}>(no email)</span>}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#374151' }}>
                        {o.pickup_date && <div>{o.pickup_date}</div>}
                        {o.location && <div style={{ color: '#6b7280' }}>{o.location}</div>}
                        {o.num_packages && <div style={{ color: '#6b7280' }}>{o.num_packages} pkg(s)</div>}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 700, color: '#dc2626', fontSize: '0.95rem' }}>
                        ${Number(o.amount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.7rem', color: '#6b7280', fontFamily: 'monospace' }}>
                        {(o.session_id || '').slice(0, 22)}…
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                          <a
                            href={o.stripe_dashboard_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`stripe-link-${o.session_id}`}
                            style={{
                              padding: '0.4rem 0.75rem',
                              backgroundColor: '#6366f1',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '0.7rem',
                              textDecoration: 'none',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                            }}
                          >
                            <ExternalLink size={12} /> Stripe
                          </a>
                          <Button
                            size="sm"
                            onClick={() => markReconciled(o.session_id)}
                            disabled={reconcilingId === o.session_id}
                            data-testid={`reconcile-btn-${o.session_id}`}
                            style={{
                              padding: '0.4rem 0.75rem',
                              fontSize: '0.7rem',
                              backgroundColor: '#10b981',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          >
                            {reconcilingId === o.session_id ? '…' : 'Mark Resolved'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {orphans.length > 0 && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem 1.25rem',
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '8px',
        }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <AlertCircle size={20} color="#b45309" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
              <strong>How to recover an orphan:</strong> Click <em>Stripe</em> to view the payment in Stripe Dashboard for the customer&apos;s email and amount, then email the customer to collect their pickup details (or refund them). Once handled, click <em>Mark Resolved</em> to remove it from this list.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrphanedPaymentsTab;
